import { createComponentLogger } from '../utils/logger';

const logger = createComponentLogger('ConsultationPersistence');

const DB_NAME = 'consultation-data';
const DB_VERSION = 1;
const STORE_NAME = 'consultation';
const CURRENT_CONSULTATION_KEY = 'current_consultation_id';

export interface PersistedData {
  consultationId: string;
  audioData: ArrayBuffer | null;
  audioMimeType: string | null;
  audioDurationSeconds: number;
  summary: unknown | null;
  summaryFile: string | null;
  createdAt: number;
  updatedAt: number;
}

export interface CompleteConsultationData {
  consultationId: string;
  audio: { blob: Blob; durationSeconds: number } | null;
  summary: unknown | null;
  summaryFile: string | null;
}

let dbInstance: IDBDatabase | null = null;

async function openDatabase(): Promise<IDBDatabase> {
  if (dbInstance) return dbInstance;

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      dbInstance.onclose = () => {
        dbInstance = null;
      };
      resolve(dbInstance);
    };
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'consultationId' });
      }
    };
  });
}

export async function initializeForConsultation(consultationId: string): Promise<void> {
  try {
    const currentId = localStorage.getItem(CURRENT_CONSULTATION_KEY);
    if (currentId && currentId !== consultationId) {
      logger.log('New consultation, clearing old data', { old: currentId, new: consultationId });
      await clearConsultationData(currentId);
    }
    localStorage.setItem(CURRENT_CONSULTATION_KEY, consultationId);
  } catch (error) {
    logger.error('Failed to initialize persistence', { error, consultationId });
  }
}

export async function saveAudio(
  consultationId: string,
  audioBlob: Blob,
  durationSeconds: number
): Promise<void> {
  try {
    const db = await openDatabase();
    const audioData = await audioBlob.arrayBuffer();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const existing = await new Promise<PersistedData | undefined>((resolve, reject) => {
      const request = store.get(consultationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const data: PersistedData = {
      consultationId,
      audioData,
      audioMimeType: audioBlob.type || 'audio/webm;codecs=opus',
      audioDurationSeconds: durationSeconds,
      summary: existing?.summary || null,
      summaryFile: existing?.summaryFile || null,
      createdAt: existing?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    logger.log('Audio saved', { consultationId, size: audioData.byteLength, durationSeconds });
  } catch (error) {
    logger.error('Failed to save audio', { error, consultationId });
    throw error;
  }
}

export async function saveSummary(
  consultationId: string,
  summary: unknown,
  summaryFile?: string | null
): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const existing = await new Promise<PersistedData | undefined>((resolve, reject) => {
      const request = store.get(consultationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    const data: PersistedData = {
      consultationId,
      audioData: existing?.audioData || null,
      audioMimeType: existing?.audioMimeType || null,
      audioDurationSeconds: existing?.audioDurationSeconds || 0,
      summary,
      summaryFile: summaryFile || null,
      createdAt: existing?.createdAt || Date.now(),
      updatedAt: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(data);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    logger.log('Summary saved', { consultationId });
  } catch (error) {
    logger.error('Failed to save summary', { error, consultationId });
    throw error;
  }
}

export async function loadAudio(
  consultationId: string
): Promise<{ blob: Blob; durationSeconds: number } | null> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const data = await new Promise<PersistedData | undefined>((resolve, reject) => {
      const request = store.get(consultationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!data?.audioData || !data.audioMimeType) return null;

    const blob = new Blob([data.audioData], { type: data.audioMimeType });
    return { blob, durationSeconds: data.audioDurationSeconds };
  } catch (error) {
    logger.error('Failed to load audio', { error, consultationId });
    return null;
  }
}

export async function loadSummary(consultationId: string): Promise<{
  summary: unknown;
  summaryFile: string | null;
} | null> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const data = await new Promise<PersistedData | undefined>((resolve, reject) => {
      const request = store.get(consultationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (!data?.summary) return null;
    return {
      summary: data.summary,
      summaryFile: data.summaryFile,
    };
  } catch (error) {
    logger.error('Failed to load summary', { error, consultationId });
    return null;
  }
}

export async function loadCompleteConsultation(
  consultationId: string
): Promise<CompleteConsultationData> {
  const [audioData, summaryData] = await Promise.all([
    loadAudio(consultationId),
    loadSummary(consultationId),
  ]);
  return {
    consultationId,
    audio: audioData,
    summary: summaryData?.summary || null,
    summaryFile: summaryData?.summaryFile || null,
  };
}

export async function hasPersistedData(
  consultationId: string
): Promise<{ hasAudio: boolean; hasSummary: boolean }> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const data = await new Promise<PersistedData | undefined>((resolve, reject) => {
      const request = store.get(consultationId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return { hasAudio: !!data?.audioData, hasSummary: !!data?.summary };
  } catch (error) {
    logger.error('Failed to check persisted data', { error, consultationId });
    return { hasAudio: false, hasSummary: false };
  }
}

export async function clearAllData(): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.clear();
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    localStorage.removeItem(CURRENT_CONSULTATION_KEY);
    logger.log('All data cleared');
  } catch (error) {
    logger.error('Failed to clear data', { error });
  }
}

export async function clearConsultationData(consultationId: string): Promise<void> {
  try {
    const db = await openDatabase();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(consultationId);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    logger.log('Consultation data cleared', { consultationId });
  } catch (error) {
    logger.error('Failed to clear consultation data', { error, consultationId });
  }
}
